import { useState, useEffect, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Trash2,
  Clock,
  Search,
  Settings,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Mail,
  Smartphone,
  Monitor,
  X,
  Filter,
} from 'lucide-react';
import { notificationsApi, Notification, NotificationPreference } from '@/api/endpoints';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationSSE, SSENotification } from '@/hooks/useNotificationSSE';
import {
  getCategoryConfig,
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
} from '@/config/notificationConfig';

export const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreference | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const perPage = 20;

  // SSE real-time notifications (only on page 1)
  const { connected } = useNotificationSSE({
    onNotification: (sseNotif: SSENotification) => {
      if (page !== 1) return;
      const newNotif: Notification = {
        id: sseNotif.id,
        tenant_id: '',
        user_id: '',
        type: 'in_app',
        title: sseNotif.title,
        message: sseNotif.message,
        category: sseNotif.category,
        action_url: sseNotif.action_url,
        icon: sseNotif.icon,
        data: sseNotif.data,
        status: 'sent',
        created_at: sseNotif.created_at,
      };
      setNotifications((prev) => [newNotif, ...prev.slice(0, perPage - 1)]);
      setUnreadCount((prev) => prev + 1);
    },
  });

  useEffect(() => {
    fetchNotifications();
  }, [filter, page]);

  useEffect(() => {
    if (showSettings) {
      fetchPreferences();
    }
  }, [showSettings]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsApi.list({
        limit: perPage,
        offset: (page - 1) * perPage,
        unread_only: filter === 'unread',
      });
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await notificationsApi.getPreferences();
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: 'read' as const } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' as const })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await notificationsApi.delete(id);
      const notification = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (notification?.status !== 'read') {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.status !== 'read') {
      handleMarkAsRead(notification.id);
    }
    if (notification.action_url) {
      try {
        // Extract path from full URLs (e.g. http://localhost:5174/student/browse-exams -> /student/browse-exams)
        const url = new URL(notification.action_url, window.location.origin);
        navigate(url.pathname);
      } catch {
        // Already a relative path
        navigate(notification.action_url);
      }
    }
  };

  const handleSavePreferences = async () => {
    if (!preferences) return;
    try {
      setSavingPrefs(true);
      await notificationsApi.updatePreferences(preferences);
      setShowSettings(false);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setSavingPrefs(false);
    }
  };

  const getNotificationIcon = (notification: Notification) => {
    const config = getCategoryConfig(notification.category);
    return createElement(config.icon, { className: `w-5 h-5 ${config.colorClass}` });
  };

  const filteredNotifications = notifications
    .filter((n) =>
      n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.message.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((n) => categoryFilter === 'all' || n.category === categoryFilter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">Notifications</h1>
            {connected && (
              <span className="flex items-center gap-1 text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <p className="text-gray-400 mt-1 text-sm">
            {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] font-medium text-sm flex items-center gap-2"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all as read
            </button>
          )}
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium hover:from-teal-600 hover:to-cyan-600 shadow-lg shadow-teal-500/20 transition-all flex items-center gap-2 text-sm"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
            />
          </div>

          {/* Category filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="pl-9 pr-8 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/50"
            >
              <option value="all">All Categories</option>
              {Object.entries(NOTIFICATION_CATEGORIES).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 p-1 bg-[#0d0d12] rounded-xl border border-white/[0.06]">
            <button
              onClick={() => { setFilter('all'); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === 'all'
                  ? 'bg-teal-500/20 text-teal-400'
                  : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              All
            </button>
            <button
              onClick={() => { setFilter('unread'); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                filter === 'unread'
                  ? 'bg-teal-500/20 text-teal-400'
                  : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              Unread
              {unreadCount > 0 && (
                <span className="bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-white font-semibold text-lg mb-2">No notifications</p>
            <p className="text-gray-400 text-sm">
              {filter === 'unread' ? 'You have no unread notifications' : 'Your notification inbox is empty'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`px-6 py-4 hover:bg-white/[0.02] transition-colors cursor-pointer ${
                  notification.status !== 'read' ? 'bg-teal-500/[0.03]' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl ${getCategoryConfig(notification.category).bgClass} flex items-center justify-center flex-shrink-0`}>
                    {getNotificationIcon(notification)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className={`font-medium ${notification.status !== 'read' ? 'text-white' : 'text-gray-300'}`}>
                          {notification.title}
                        </h4>
                        <p className="text-sm text-gray-400 mt-1">{notification.message}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {notification.status !== 'read' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                            className="p-2 rounded-lg text-teal-400 hover:bg-teal-500/10 transition-colors"
                            title="Mark as read"
                          >
                            <CheckCheck className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}
                          className="p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      {notification.category && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${getCategoryConfig(notification.category).bgClass} ${getCategoryConfig(notification.category).colorClass}`}>
                          {getCategoryConfig(notification.category).label}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                        notification.status === 'read'
                          ? 'bg-gray-500/10 text-gray-400'
                          : notification.status === 'sent'
                          ? 'bg-teal-500/10 text-teal-400'
                          : notification.status === 'failed'
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-amber-500/10 text-amber-400'
                      }`}>
                        {notification.status}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredNotifications.length > 0 && (
          <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, filteredNotifications.length)} notifications
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-400">Page {page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={filteredNotifications.length < perPage}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#12121a] border border-white/[0.08] rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Notification Settings</h2>
                <p className="text-gray-400 text-sm mt-1">Configure how you receive notifications</p>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Channels */}
              <div className="space-y-4">
                <h3 className="text-white font-semibold">Notification Channels</h3>

                <label className="flex items-center justify-between p-4 bg-[#0d0d12] rounded-xl border border-white/[0.06] cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
                      <Monitor className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">In-App Notifications</p>
                      <p className="text-gray-500 text-xs">Show notifications in the app</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences?.in_app_enabled ?? true}
                    onChange={(e) => setPreferences((p) => p ? { ...p, in_app_enabled: e.target.checked } : null)}
                    className="w-5 h-5 rounded bg-[#0d0d12] border-white/[0.2] text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-[#0d0d12] rounded-xl border border-white/[0.06] cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Email Notifications</p>
                      <p className="text-gray-500 text-xs">Receive notifications via email</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences?.email_enabled ?? true}
                    onChange={(e) => setPreferences((p) => p ? { ...p, email_enabled: e.target.checked } : null)}
                    className="w-5 h-5 rounded bg-[#0d0d12] border-white/[0.2] text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-[#0d0d12] rounded-xl border border-white/[0.06] cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Push Notifications</p>
                      <p className="text-gray-500 text-xs">Receive push notifications on your device</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences?.push_enabled ?? false}
                    onChange={(e) => setPreferences((p) => p ? { ...p, push_enabled: e.target.checked } : null)}
                    className="w-5 h-5 rounded bg-[#0d0d12] border-white/[0.2] text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                  />
                </label>
              </div>

              {/* Category Preferences */}
              <div className="space-y-4">
                <h3 className="text-white font-semibold">Category Preferences</h3>
                <div className="space-y-2">
                  {(Object.entries(NOTIFICATION_CATEGORIES) as [NotificationCategory, typeof NOTIFICATION_CATEGORIES[NotificationCategory]][]).map(([key, config]) => (
                    <label key={key} className="flex items-center justify-between p-3 bg-[#0d0d12] rounded-lg border border-white/[0.06] cursor-pointer">
                      <div className="flex items-center gap-2">
                        {createElement(config.icon, { className: `w-4 h-4 ${config.colorClass}` })}
                        <span className="text-sm text-gray-300">{config.label}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences?.preferences?.[key] !== false}
                        onChange={(e) => setPreferences((p) => p ? {
                          ...p,
                          preferences: { ...p.preferences, [key]: e.target.checked },
                        } : null)}
                        className="w-4 h-4 rounded bg-[#0d0d12] border-white/[0.2] text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Quiet Hours */}
              <div className="space-y-4">
                <label className="flex items-center justify-between">
                  <h3 className="text-white font-semibold">Quiet Hours</h3>
                  <input
                    type="checkbox"
                    checked={preferences?.quiet_hours?.enabled ?? false}
                    onChange={(e) => setPreferences((p) => p ? {
                      ...p,
                      quiet_hours: {
                        ...p.quiet_hours,
                        enabled: e.target.checked,
                        start_time: p.quiet_hours?.start_time || '22:00',
                        end_time: p.quiet_hours?.end_time || '08:00',
                        timezone: p.quiet_hours?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                      },
                    } : null)}
                    className="w-5 h-5 rounded bg-[#0d0d12] border-white/[0.2] text-teal-500 focus:ring-teal-500 focus:ring-offset-0"
                  />
                </label>
                {preferences?.quiet_hours?.enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={preferences.quiet_hours.start_time}
                        onChange={(e) => setPreferences((p) => p ? {
                          ...p,
                          quiet_hours: { ...p.quiet_hours!, start_time: e.target.value },
                        } : null)}
                        className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">End Time</label>
                      <input
                        type="time"
                        value={preferences.quiet_hours.end_time}
                        onChange={(e) => setPreferences((p) => p ? {
                          ...p,
                          quiet_hours: { ...p.quiet_hours!, end_time: e.target.value },
                        } : null)}
                        className="w-full px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-white/[0.06] flex gap-3">
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreferences}
                disabled={savingPrefs}
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium text-sm flex items-center justify-center gap-2"
              >
                {savingPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
